/**
 * facebookPixel.js
 *
 * Centralised, production-safe Meta Pixel helper functions.
 * Each helper checks window.fbq before calling it so the app
 * never throws if the Pixel script is blocked by an ad blocker
 * or browser extension.
 *
 * Base Pixel Code + fbq('init') are already in public/index.html.
 * These helpers ONLY add event tracking on top of that.
 *
 * Usage:
 *   import { trackLead, trackWhatsAppClick, trackCallClick,
 *            trackEmailClick, trackViewContent } from '../../utils/facebookPixel';
 */

/**
 * Safely fires a standard Meta Pixel event.
 * @param {string} eventName  - Standard Meta event name e.g. 'Lead', 'ViewContent'
 * @param {object} [params]   - Optional event parameters object
 */
export const trackEvent = (eventName, params = {}) => {
  if (typeof window.fbq === 'function') {
    window.fbq('track', eventName, params);
  }
};

/**
 * Safely fires a custom Meta Pixel event.
 * @param {string} eventName  - Custom event name e.g. 'WhatsAppClick'
 * @param {object} [params]   - Optional event parameters object
 */
export const trackCustomEvent = (eventName, params = {}) => {
  if (typeof window.fbq === 'function') {
    window.fbq('trackCustom', eventName, params);
  }
};

// ─── Convenience wrappers ────────────────────────────────────────────────────

/**
 * Track a successful contact form submission.
 * Fire ONLY after the API responds with success — not on button click.
 */
export const trackLead = () => trackEvent('Lead');

/**
 * Track when the WhatsApp link is clicked.
 */
export const trackWhatsAppClick = () => trackCustomEvent('WhatsAppClick');

/**
 * Track when the phone (tel:) link is clicked.
 */
export const trackCallClick = () => trackCustomEvent('CallClick');

/**
 * Track when the email (mailto:) link is clicked.
 */
export const trackEmailClick = () => trackCustomEvent('EmailClick');

/**
 * Track a page view of a content-rich page (Portfolio, Service pages).
 * @param {string} [contentName] - Human-readable label for the page
 */
export const trackViewContent = (contentName = '') =>
  trackEvent('ViewContent', contentName ? { content_name: contentName } : {});
